import type { SiteBriefTaskResponse, SiteBriefType, SiteBriefListResponseType } from './api.types';
import React, { useEffect, useState, useSyncExternalStore } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';

declare const BACKEND_URL: string;
export const realtime = io(BACKEND_URL, { autoConnect: false, transports: ['websocket'], auth: callback => callback({ token: localStorage.getItem('authToken') || '' }) });
let state = { connected: false, ready: false, status: 'Disconnected', latency: null as number | null, lastChecked: null as number | null };
const listeners = new Set<() => void>();
let connectionEpoch = 0;
let readinessVersion = 0;
const update = (value: Partial<typeof state>) => { state = { ...state, ...value }; listeners.forEach(listener => listener()); };
export const useRealtime = () => useSyncExternalStore(listener => { listeners.add(listener); return () => { listeners.delete(listener); }; }, () => state);
export async function checkRealtime() {
  if (!realtime.connected) { update({ status: 'Connecting' }); realtime.connect(); return; }
  const start = Date.now();
  const epoch = connectionEpoch;
  const version = readinessVersion;
  try {
    const result = await realtime.timeout(5000).emitWithAck('system:connection-check');
    if (!realtime.connected || epoch !== connectionEpoch) return;
    update({ ...(version === readinessVersion ? { ready: result.httpStatus === 200 && result.body.notifications_ready } : {}),
      latency: Date.now() - start, lastChecked: Date.now(),
      status: result.httpStatus === 200 ? 'Connected' : 'Authentication failed' });
  } catch {
    if (!realtime.connected || epoch !== connectionEpoch || version !== readinessVersion) return;
    update({ ready: false, status: 'Connection check failed', latency: null, lastChecked: Date.now() });
  }
}
realtime.on('connect', () => {
  connectionEpoch++;
  update({ connected: true, ready: false, status: 'Connected' });
  void checkRealtime();
});
realtime.on('disconnect', () => {
  connectionEpoch++;
  update({ connected: false, ready: false, status: 'Disconnected' });
});
realtime.on('connect_error', () => update({ connected: false, ready: false, status: 'Connection failed' }));
realtime.on('system:notifications-ready', (message: { notifications_ready: boolean }) => {
  if (!realtime.connected || typeof message?.notifications_ready !== 'boolean') return;
  readinessVersion++;
  update({ ready: message.notifications_ready, status: 'Connected' });
});

export function RealtimeProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  useEffect(() => {
    if (!isAuthenticated) return;
    realtime.connect();
    return () => { realtime.disconnect(); };
  }, [isAuthenticated]);
  return <>{children}</>;
}

export type BriefSnapshot = {
  progress?: SiteBriefTaskResponse;
  detail?: { status: string; site_brief: SiteBriefType };
  list?: SiteBriefListResponseType;
};
const subscriptionCounts = new Map<string, number>();
export function subscribeBrief(input: { sessionId: string } | { scope: 'own' | 'all' }, refresh: () => void, applySnapshot?: (snapshot: BriefSnapshot) => void) {
  let active = true;
  const key = JSON.stringify(input);
  subscriptionCounts.set(key, (subscriptionCounts.get(key) || 0) + 1);
  const subscribe = () => {
    const epoch = connectionEpoch;
    if (realtime.connected) realtime.timeout(5000).emit('site-brief:subscribe', input, (error: Error | null, result: { httpStatus: number }) => {
      if (active && realtime.connected && epoch === connectionEpoch && !error && result?.httpStatus === 200) refresh();
    });
  };
  let timer: ReturnType<typeof setTimeout> | undefined;
  const changed = () => {
    if (active && !timer) timer = setTimeout(() => { timer = undefined; if (active) refresh(); }, 150);
  };
  realtime.on('connect', subscribe);
  realtime.on('disconnect', changed);
  let previousReady = state.ready;
  const readiness = () => { if (previousReady !== state.ready) { previousReady = state.ready; changed(); } };
  listeners.add(readiness);
  const receive = (event: { subscription?: typeof input; snapshot?: BriefSnapshot }) => {
    if (!active || (event?.subscription && JSON.stringify(event.subscription) !== key)) {
      return;
    }
    const complete = event?.subscription && ('sessionId' in input
      ? event.snapshot?.progress : event.snapshot?.list && event.snapshot?.detail);
    if (complete && event.snapshot && applySnapshot) {
      applySnapshot(event.snapshot);
    } else {
      changed();
    }
  };
  realtime.on('site-brief:changed', receive);
  subscribe();
  return () => {
    active = false;
    clearTimeout(timer);
    realtime.off('connect', subscribe);
    realtime.off('disconnect', changed);
    listeners.delete(readiness);
    realtime.off('site-brief:changed', receive);
    const remaining = (subscriptionCounts.get(key) || 1) - 1;
    if (remaining) subscriptionCounts.set(key, remaining);
    else { subscriptionCounts.delete(key); if (realtime.connected) realtime.emit('site-brief:unsubscribe', input); }
  };
}
export const reconciliationDelay = (fallback: number) => state.connected && state.ready ? 30000 : fallback;
export async function socketCommand(event: string, payload: unknown): Promise<{ httpStatus: number; body: unknown } | null> {
  if (!realtime.connected) return null;
  try { return await realtime.volatile.timeout(10000).emitWithAck(event, payload); }
  catch { return null; }
}

export function WebsocketDiagnostics() {
  const connection = useRealtime();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (connection.lastChecked === null) return;
    // Display-only timer: never sends a connection check or any network request.
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [connection.lastChecked]);

  const ready = connection.connected && connection.ready;
  const elapsedSeconds = connection.lastChecked === null ? null : Math.max(0, Math.floor((now - connection.lastChecked) / 1000));

  return (
    <div className="rounded-xl border border-[#dbe7ff] bg-[#f8fbff] p-3">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#4f6ea8]">
            Connect to WebSockets
          </div>
          <div className="mt-1 text-[13px] font-semibold text-[#27344a]">
            {connection.status}{connection.connected && connection.latency !== null ? ` · latency is ${connection.latency} ms` : ''}
          </div>
          <div className="mt-1 text-[12px] text-[#5f6f89]">
            {elapsedSeconds === null ? 'Not checked' : `Checked ${elapsedSeconds}s ago`}
          </div>
        </div>
        <span role="status" className="shrink-0 rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-[#5f6f89]">
          {ready ? 'Ready' : 'Fallback'}
        </span>
      </div>
      <button
        type="button"
        onClick={() => { void checkRealtime(); }}
        className="h-11 w-full rounded-lg bg-[#165bfb] font-semibold text-white transition-colors hover:bg-[#1246d7] active:bg-[#0d38b0]"
      >
        {connection.connected ? 'Check connection' : 'Connect / Reconnect'}
      </button>
    </div>
  );
}
